/**
 * nl-BE locale formatters.
 *
 * Mandatory across the UI — NEVER use en-US Intl defaults or date-fns without
 * the `nl` locale. See PROJECT_BRIEF.md §10 and design-system.md.
 *
 *  - Dates / times: `date-fns` with locale `nl`, pattern "d MMM yyyy" by default.
 *  - Numbers: `Intl.NumberFormat("nl-BE")` — thin space as thousands separator,
 *    decimal comma.
 *  - Percentages: 1 fractional digit, "94,2 %".
 *  - Relative time: `formatDistanceToNow` with `nl` + `addSuffix`.
 *  - Bytes: IEC binary (1 KiB = 1024 B), one fractional digit on units > 1 KiB.
 */

import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';

// ---------------------------------------------------------------------------
// Intl formatters — singletons. `Intl.NumberFormat` construction is expensive
// enough that re-using one instance per format kind is a measurable win.
// ---------------------------------------------------------------------------

const NUMBER_FMT = new Intl.NumberFormat('nl-BE');

const PERCENT_FMT_1 = new Intl.NumberFormat('nl-BE', {
  style: 'percent',
  maximumFractionDigits: 1,
  minimumFractionDigits: 1,
});

const DECIMAL_FMT_1 = new Intl.NumberFormat('nl-BE', {
  maximumFractionDigits: 1,
  minimumFractionDigits: 1,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDate(d: Date | string): Date {
  return typeof d === 'string' ? parseISO(d) : d;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** "22 mei 2026" by default. Accepts ISO strings or Date objects. */
export function formatDate(d: Date | string, fmt: string = 'd MMM yyyy'): string {
  return format(toDate(d), fmt, { locale: nl });
}

/** "22 mei 2026 14:38" — 24h clock, no seconds. */
export function formatDateTime(d: Date | string): string {
  return format(toDate(d), 'd MMM yyyy HH:mm', { locale: nl });
}

/** Integer or float with nl-BE separators: "5 678", "1,5". */
export function formatNumber(n: number): string {
  return NUMBER_FMT.format(n);
}

/**
 * Fraction (0..1) → percentage with one fractional digit, e.g. `0.942` →
 * "94,2 %". Pass the raw fraction; do NOT multiply by 100 yourself.
 */
export function formatPercent(frac: number): string {
  // Intl renders "94,2 %" with a non-breaking space in nl-BE — good for prose.
  return PERCENT_FMT_1.format(frac);
}

/** "5 min geleden", "2 dagen geleden", "ongeveer 1 uur geleden". */
export function formatRelativeTime(d: Date | string): string {
  return formatDistanceToNow(toDate(d), { locale: nl, addSuffix: true });
}

/**
 * IEC binary bytes ("KiB", "MiB", …) with nl-BE decimal comma. Bytes < 1 KiB
 * render without a fractional digit ("512 B"); larger units use one digit
 * ("1,5 MiB"). Negative inputs are absolute-valued; NaN/non-finite → "0 B".
 */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n)) return '0 B';
  const abs = Math.abs(n);
  if (abs < 1024) return `${NUMBER_FMT.format(Math.round(abs))} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  let value = abs / 1024;
  let unitIdx = 0;
  while (value >= 1024 && unitIdx < units.length - 1) {
    value /= 1024;
    unitIdx += 1;
  }
  return `${DECIMAL_FMT_1.format(value)} ${units[unitIdx]}`;
}
